import React, { useState } from "react";

import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { TextOverlay, TextAnimation, BackgroundStyle } from "../../types";

const ANIMATION_OPTIONS: { value: TextAnimation; label: string }[] = [
  { value: "none", label: "None" },
  { value: "fade-in", label: "Fade In" },
  { value: "fade-out", label: "Fade Out" },
  { value: "slide-up", label: "Slide Up" },
  { value: "slide-down", label: "Slide Down" },
  { value: "slide-left", label: "Slide Left" },
  { value: "slide-right", label: "Slide Right" },
  { value: "zoom-in", label: "Zoom In" },
  { value: "zoom-out", label: "Zoom Out" },
  { value: "pop", label: "Pop" },
  { value: "typewriter", label: "Typewriter" },
  { value: "bounce", label: "Bounce" },
  { value: "pulse", label: "Pulse" },
  { value: "rotate", label: "Rotate" },
];

const FONT_OPTIONS = [
  "Inter",
  "Montserrat",
  "Outfit",
  "Bebas Neue",
  "Oswald",
  "Josefin Sans",
  "Playfair Display",
  "Arvo",
];

interface TextOverlayPanelProps {
  textOverlays: TextOverlay[];
  videoDurationMs: number;
  currentTimeMs: number;
  onAddText: (overlay: TextOverlay) => void;
  onRemoveText: (id: string) => void;
  onUpdateText: (id: string, updates: Partial<TextOverlay>) => void;
}

const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const parseTime = (timeStr: string): number => {
  const parts = timeStr.split(":");
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10) || 0;
    const s = parseInt(parts[1], 10) || 0;
    return (m * 60 + s) * 1000;
  }
  return 0;
};

export default function TextOverlayPanel({
  textOverlays,
  videoDurationMs,
  currentTimeMs,
  onAddText,
  onRemoveText,
  onUpdateText,
}: TextOverlayPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(
    textOverlays.length === 1 ? (textOverlays[0]?.id ?? null) : null,
  );

  const handleAddText = () => {
    const overlay: TextOverlay = {
      id: crypto.randomUUID(),
      text: "Your text here",
      x: 30,
      y: 40,
      width: 40,
      height: 15,
      startMs: currentTimeMs,
      endMs: videoDurationMs,
      fontSize: 24,
      fontFamily: "Inter",
      color: "#ffffff",
      backgroundColor: "rgba(0,0,0,0.5)",
      backgroundStyle: BackgroundStyle.SOLID,
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
      animation: "none",
    };
    onAddText(overlay);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddText}
        fullWidth
      >
        Add Text
      </Button>

      {textOverlays.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No text overlays yet. Click "Add Text" to get started.
        </Typography>
      )}

      {textOverlays.map((overlay) => (
        <Accordion
          key={overlay.id}
          expanded={expandedId === overlay.id}
          onChange={() =>
            setExpandedId((prev) => (prev === overlay.id ? null : overlay.id))
          }
          sx={{
            border: "1px solid #eee",
            borderRadius: 1,
            mb: 1,
            "&:before": { display: "none" },
            boxShadow: "none",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                pr: 1,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ flex: 1, mr: 1 }}
              >
                {overlay.text || "Text Overlay"}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveText(overlay.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, flexDirection: "column" }}>
            <TextField
              size="small"
              label="Text"
              multiline
              minRows={2}
              maxRows={4}
              value={overlay.text}
              onChange={(e) =>
                onUpdateText(overlay.id, { text: e.target.value })
              }
              fullWidth
              sx={{ mb: 1 }}
            />

            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <InputLabel sx={{ fontSize: 11 }}>Font</InputLabel>
              <Select
                value={overlay.fontFamily}
                label="Font"
                onChange={(e) =>
                  onUpdateText(overlay.id, {
                    fontFamily: e.target.value as string,
                  })
                }
                sx={{ fontSize: 13 }}
              >
                {FONT_OPTIONS.map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <InputLabel sx={{ fontSize: 11 }}>Animation</InputLabel>
              <Select
                value={overlay.animation || "none"}
                label="Animation"
                onChange={(e) =>
                  onUpdateText(overlay.id, {
                    animation: e.target.value as TextAnimation,
                  })
                }
                sx={{ fontSize: 13 }}
              >
                {ANIMATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Start"
                defaultValue={formatTime(overlay.startMs)}
                onBlur={(e) =>
                  onUpdateText(overlay.id, {
                    startMs: parseTime(e.target.value),
                  })
                }
                sx={{ width: 70 }}
                inputProps={{ style: { fontSize: 12 } }}
                InputLabelProps={{ style: { fontSize: 11 } }}
              />
              <TextField
                size="small"
                label="End"
                defaultValue={formatTime(overlay.endMs)}
                onBlur={(e) =>
                  onUpdateText(overlay.id, {
                    endMs: parseTime(e.target.value),
                  })
                }
                sx={{ width: 70 }}
                inputProps={{ style: { fontSize: 12 } }}
                InputLabelProps={{ style: { fontSize: 11 } }}
              />
              <TextField
                size="small"
                label="Size"
                type="number"
                value={overlay.fontSize}
                onChange={(e) =>
                  onUpdateText(overlay.id, {
                    fontSize: Math.max(8, parseInt(e.target.value, 10) || 24),
                  })
                }
                sx={{ width: 65 }}
                inputProps={{ style: { fontSize: 12 }, min: 8, max: 120 }}
                InputLabelProps={{ style: { fontSize: 11 } }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption">Color</Typography>
                <input
                  type="color"
                  value={overlay.color}
                  onChange={(e) =>
                    onUpdateText(overlay.id, { color: e.target.value })
                  }
                  style={{
                    width: 28,
                    height: 28,
                    border: "none",
                    cursor: "pointer",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption">Bg</Typography>
                <input
                  type="color"
                  value={
                    overlay.backgroundColor === "transparent" ||
                    overlay.backgroundColor.startsWith("rgba")
                      ? "#000000"
                      : overlay.backgroundColor
                  }
                  onChange={(e) =>
                    onUpdateText(overlay.id, {
                      backgroundColor: `${e.target.value}80`,
                    })
                  }
                  disabled={overlay.backgroundColor === "transparent"}
                  style={{
                    width: 28,
                    height: 28,
                    border: "none",
                    cursor: "pointer",
                    opacity:
                      overlay.backgroundColor === "transparent" ? 0.3 : 1,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    onUpdateText(overlay.id, {
                      backgroundColor:
                        overlay.backgroundColor === "transparent"
                          ? "rgba(0,0,0,0.5)"
                          : "transparent",
                    })
                  }
                  sx={{
                    fontSize: 10,
                    border:
                      overlay.backgroundColor === "transparent"
                        ? "2px solid"
                        : "1px solid #ccc",
                    borderRadius: 1,
                    width: 28,
                    height: 28,
                    textDecoration:
                      overlay.backgroundColor === "transparent"
                        ? "line-through"
                        : "none",
                  }}
                  title="Toggle transparent background"
                >
                  Bg
                </IconButton>
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  onUpdateText(overlay.id, { bold: !overlay.bold })
                }
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  border: overlay.bold ? "2px solid" : "1px solid #ccc",
                  borderRadius: 1,
                  width: 28,
                  height: 28,
                }}
              >
                B
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  onUpdateText(overlay.id, { italic: !overlay.italic })
                }
                sx={{
                  fontStyle: "italic",
                  fontSize: 14,
                  border: overlay.italic ? "2px solid" : "1px solid #ccc",
                  borderRadius: 1,
                  width: 28,
                  height: 28,
                }}
              >
                I
              </IconButton>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
